// Complete Listing - Process pending listing after signup
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/firebaseAuth';
import { insertDocument } from '../../utility/firebaseHelpers';
import { uploadFile } from '../../utility/firebaseStorageHelpers';
import { serverTimestamp } from 'firebase/firestore';
import { Listing } from '../../components/property/property-card/PropertyCardTypes';
import { generateNumericPropertyId } from '../../utility/property-utilities/generateNumericPropertyId';

const CompleteListing: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const completePendingListing = async () => {
      // Prevent double execution
      if (isProcessing) return;

      if (!currentUser || !userProfile) {
        navigate('/signup');
        return;
      }

      // Mark as processing immediately to prevent double execution
      setIsProcessing(true);

      try {
        // Retrieve pending listing data
        const pendingListingData = localStorage.getItem('pendingListing');
        const pendingImagesData = localStorage.getItem('pendingListingImages');

        if (!pendingListingData) {
          // If no pending listing, redirect to create listing page
          navigate('/add-listing');
          return;
        }

        // Parse the data before clearing (in case we need it for error handling)
        const listingDraft = JSON.parse(pendingListingData);

        // Clear localStorage right away to prevent duplicates if page refreshes
        localStorage.removeItem('pendingListing');
        localStorage.removeItem('pendingListingImages');

        // Check if listing is not too old (24 hours)
        const draftAge =
          new Date().getTime() - new Date(listingDraft.timestamp).getTime();
        if (draftAge > 24 * 60 * 60 * 1000) {
          setError(
            'Your listing draft has expired. Please create a new listing.'
          );
          return;
        }

        // Generate property ID
        const propertyId = generateNumericPropertyId(14);

        // Process and upload images
        let imageUrls: string[] = [];
        if (pendingImagesData) {
          const imageData = JSON.parse(pendingImagesData);

          // Convert base64 back to File objects and upload
          const uploadPromises = imageData.map(
            async (img: any, index: number) => {
              // Convert data URL to Blob
              const response = await fetch(img.dataUrl);
              const blob = await response.blob();
              const file = new File([blob], img.name, { type: img.type });

              // Upload to Firebase Storage
              return uploadFile(
                file,
                `listings/${propertyId}/${Date.now()}_${index}_${img.name}`
              );
            }
          );

          imageUrls = await Promise.all(uploadPromises);
        }

        // Set expiration date
        const expirationDate = new Date();
        expirationDate.setMonth(expirationDate.getMonth() + 3);

        // Create listing object
        const listingData: Listing = {
          id: propertyId,
          title: listingDraft.title,
          type: listingDraft.type,
          price: Number(listingDraft.price),
          location: listingDraft.location,
          bedrooms: listingDraft.bedrooms,
          bathrooms: listingDraft.bathrooms,
          size: listingDraft.size,
          status: 'active',
          virtualTourUrl: listingDraft.virtualTourUrl || '',
          listingIntent: listingDraft.listingIntent,
          propertyOwnerUid: currentUser.uid,
          description: listingDraft.description,
          amenities: listingDraft.amenities,
          availableFrom: new Date(listingDraft.availableFrom),
          expirationDate: expirationDate,
          berRating: listingDraft.berRating,
          images: imageUrls,
          videoUrl: listingDraft.videoUrl || '',
          user: userProfile,
          dateAdded: serverTimestamp(),
          propertyViews: 0,
          isExternalListing: false
        };

        // Insert listing into Firestore
        await insertDocument<Listing>(
          'listings',
          listingData,
          propertyId.toString()
        );

        // Navigate to success page
        navigate('/listing-success', {
          state: {
            listingSummary: {
              ...listingDraft,
              propertyId: propertyId
            }
          }
        });
      } catch (err) {
        console.error('Error completing listing:', err);
        setError('Failed to create your listing. Please try again.');
      }
    };

    completePendingListing();
  }, [currentUser, userProfile, navigate, isProcessing]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Oops! Something went wrong
            </h3>
            <p className="text-sm text-gray-500 mb-6">{error}</p>
            <button
              onClick={() => navigate('/create-listing')}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200"
            >
              Create New Listing
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg
              className="animate-spin h-6 w-6 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Creating Your Listing
          </h3>
          <p className="text-sm text-gray-500">
            Please wait while we publish your property listing...
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompleteListing;
